import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { SuiContainer, SuiTextInput } from '@splashthat/sui';

import { getFormId } from 'common/compounds/FormBuilder/selectors';
import { getFormHeaderImageStyles } from 'common/entities/form/styleConfiguration/selectors';
import {
    setFormHeaderImageSize,
    setFormHeaderImageType,
    setFormHeaderImageAltText,
    setFormHeaderImageUrl,
    setFormHeaderImageBaseUrl,
} from 'common/entities/form/styleConfiguration/actions';

import { DEFAULT_LOGO_SIZES } from 'common/entities/form/styleConfiguration/defaultValues';
import {
    NONE_TYPE,
    BANNER_TYPE,
    LOGO_TYPE,
    ALT_TEXT_LABEL,
    MEDIUM,
} from 'common/entities/form/styleConfiguration/constants';

import { containerPaddingS, containerPaddingM } from 'common/styles/variables';

import ImagePicker from 'common/components/ImagePicker';
import FormImageTypeSelector from './FormImageTypeSelector';
import FormLogoSizeDropdown from './FormLogoSizeDropdown';

import { FORM_IMAGE_QA } from '../../utils/qaIdConstants';

const baseClassName = 'FormImagePanel';

const StyledSuiContainer = styled(SuiContainer)`
    flex: 1;
    margin: 0;
    padding-top: ${containerPaddingS};
`;

const ImageEditingContainer = styled(SuiContainer)`
    padding: ${containerPaddingM};
    padding-top: 0;
`;

const FormImagePanel = ({ formId, imageUrl, imageBaseUrl, imageSize, imageType, imageAltText, ...props }) => (
    <StyledSuiContainer className={`${baseClassName}__container`}>
        <FormImageTypeSelector
            onChangeTab={(value) => props.setFormHeaderImageType(formId, value)}
            imageType={imageType}
            qaId={FORM_IMAGE_QA}
        />

        {imageType !== NONE_TYPE && (
            <ImageEditingContainer>
                {imageType === LOGO_TYPE && (
                    <FormLogoSizeDropdown
                        imageSize={imageSize}
                        logoSizes={DEFAULT_LOGO_SIZES}
                        onChangeSize={(value) => props.setFormHeaderImageSize(formId, value)}
                    />
                )}

                <ImagePicker
                    imageUrl={imageUrl}
                    imageBaseUrl={imageBaseUrl}
                    onChangeImage={(baseUrl) => props.setFormHeaderImageBaseUrl(formId, baseUrl)}
                    onEditImage={(url) => props.setFormHeaderImageUrl(formId, url)}
                    dense
                    qaId={FORM_IMAGE_QA}
                />

                {imageUrl && (
                    <SuiTextInput
                        label={ALT_TEXT_LABEL}
                        value={imageAltText}
                        onChange={(_, altText) => props.setFormHeaderImageAltText(formId, altText)}
                        data-qa={`${baseClassName}__header-alt-text-input`}
                        fullWidth
                    />
                )}
            </ImageEditingContainer>
        )}
    </StyledSuiContainer>
);

FormImagePanel.propTypes = {
    formId: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    imageBaseUrl: PropTypes.string,
    imageAltText: PropTypes.string,
    imageSize: PropTypes.string.isRequired,
    imageType: PropTypes.oneOf([NONE_TYPE, BANNER_TYPE, LOGO_TYPE]).isRequired,
    setFormHeaderImageSize: PropTypes.func.isRequired,
    setFormHeaderImageType: PropTypes.func.isRequired,
    setFormHeaderImageAltText: PropTypes.func.isRequired,
    setFormHeaderImageUrl: PropTypes.func.isRequired,
    setFormHeaderImageBaseUrl: PropTypes.func.isRequired,
};

FormImagePanel.defaultProps = {
    imageUrl: null,
    imageBaseUrl: null,
    imageAltText: null,
};

const mapStateToProps = (state) => {
    const formId = getFormId(state);
    const {
        size: imageSize = MEDIUM,
        baseUrl: imageBaseUrl,
        url: imageUrl,
        altText: imageAltText,
        type: imageType = NONE_TYPE,
    } = getFormHeaderImageStyles(state, formId);

    return {
        formId,
        imageSize,
        imageUrl,
        imageAltText,
        imageBaseUrl,
        imageType,
    };
};

const mapDispatchToProps = {
    setFormHeaderImageSize,
    setFormHeaderImageType,
    setFormHeaderImageUrl,
    setFormHeaderImageAltText,
    setFormHeaderImageBaseUrl,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormImagePanel);
